import { useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Pagination from '@/components/ui/Pagination';
import useDossierStore from '@/stores/useDossierStore';
import { formatDate, statusLabel } from '@/lib/utils';

const FILTER_TABS = ['tous', 'brouillon', 'en_cours', 'termine', 'genere', 'envoye'];
const FILTER_TAB_LABELS = {
  tous: 'Tous',
  brouillon: 'Brouillon',
  en_cours: 'En cours',
  termine: 'Terminé',
  genere: 'Généré',
  envoye: 'Envoyé',
};

export default function DossiersListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { dossiers, total, loading, error, fetchDossiers } = useDossierStore();
  const debounceRef = useRef(null);

  const search = searchParams.get('search') || '';
  const statut = searchParams.get('statut') || 'tous';
  const skip = parseInt(searchParams.get('skip') || '0', 10);
  const limit = parseInt(searchParams.get('limit') || '20', 10);
  const sortBy = searchParams.get('sort_by') || 'created_at';
  const sortOrder = searchParams.get('sort_order') || 'desc';

  const buildParams = useCallback(() => {
    const params = { skip, limit, sort_by: sortBy, sort_order: sortOrder };
    if (search) params.search = search;
    if (statut !== 'tous') params.statut = statut;
    return params;
  }, [search, statut, skip, limit, sortBy, sortOrder]);

  useEffect(() => {
    fetchDossiers(buildParams());
  }, [fetchDossiers, buildParams]);

  const updateParams = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null || v === '' || v === 'tous' || v === 0) {
          next.delete(k);
        } else {
          next.set(k, String(v));
        }
      });
      if (!('skip' in updates)) next.delete('skip');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams({ search: value });
    }, 300);
  };

  const handleTabChange = (tab) => updateParams({ statut: tab });

  const handleSort = (column) => {
    const newOrder = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
    updateParams({ sort_by: column, sort_order: newOrder });
  };

  const sortIndicator = (column) => {
    if (sortBy !== column) return '';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Dossiers</h1>
        <Link to="/dossiers/nouveau">
          <Button variant="primary" size="lg">
            <Plus className="w-4 h-4" />
            Nouveau dossier
          </Button>
        </Link>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Rechercher par référence ou client..."
          defaultValue={search}
          onChange={handleSearchChange}
          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-carex-500 focus:border-transparent text-sm"
        />
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              statut === tab
                ? 'border-carex-600 text-carex-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            {FILTER_TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">Erreur: {error}</p>
        </div>
      )}

      {loading && <p className="text-slate-600">Chargement...</p>}

      {!loading && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th
                    className="text-left py-3 px-4 font-medium text-slate-700 cursor-pointer hover:text-carex-600"
                    onClick={() => handleSort('reference')}
                  >
                    Référence{sortIndicator('reference')}
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700">Client</th>
                  <th
                    className="text-left py-3 px-4 font-medium text-slate-700 cursor-pointer hover:text-carex-600"
                    onClick={() => handleSort('statut')}
                  >
                    Statut{sortIndicator('statut')}
                  </th>
                  <th
                    className="text-left py-3 px-4 font-medium text-slate-700 cursor-pointer hover:text-carex-600"
                    onClick={() => handleSort('created_at')}
                  >
                    Date{sortIndicator('created_at')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {dossiers.map((dossier) => (
                  <tr key={dossier.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <Link
                        to={`/dossiers/${dossier.id}`}
                        className="font-medium text-carex-600 hover:text-carex-700"
                      >
                        {dossier.reference}
                      </Link>
                    </td>
                    <td className="py-3 px-4 text-slate-600">—</td>
                    <td className="py-3 px-4">
                      <Badge variant={dossier.statut}>{statusLabel(dossier.statut)}</Badge>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{formatDate(dossier.date_mandat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dossiers.length === 0 && (
            <div className="text-center py-8 text-slate-500">Aucun dossier trouvé</div>
          )}
          <Pagination
            total={total}
            skip={skip}
            limit={limit}
            onChange={(newSkip) => updateParams({ skip: newSkip })}
            onLimitChange={(newLimit) => updateParams({ limit: newLimit })}
          />
        </Card>
      )}
    </div>
  );
}
